import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Table,
  Tag,
  Modal,
  message,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
  Form,
  Divider,
  Typography,
  Badge,
  theme,
} from 'antd';
import {
  ArrowLeftOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  UndoOutlined,
  DownloadOutlined,
  UploadOutlined,
  AndroidOutlined,
  AppleOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  ClearOutlined,
  TeamOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import ImportUsersModal from './ImportUsersModal';
import SegmentSuggestions from 'components/segments/SegmentSuggestions';
import {
  fetchApplication,
  fetchAppUsers,
  fetchAppUserStats,
  deleteAppUser,
  restoreAppUser,
  exportAppUsers,
  selectCurrentApplication,
  selectAppUsers,
  selectAppUserStats,
  selectApplicationsLoading,
  clearAppUsers,
} from 'store/slices/applications.slice';
import { usePermissions } from 'hooks/usePermissions';
import { AppUser, AppUserFilter } from 'types/application.types';
import { formatters } from 'utils/formatters';
import '../../styles/Common.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

interface NullString {
  String: string;
  Valid: boolean;
}

const getNullableString = (value: string | NullString | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value || null;
  if (typeof value === 'object' && 'String' in value && 'Valid' in value) {
    return value.Valid ? value.String : null;
  }
  return null;
};

const TruncatedText: React.FC<{ text: string | null; maxLen?: number; mono?: boolean }> = ({
  text,
  maxLen = 14,
  mono = false,
}) => {
  if (!text) return <Text type="secondary">—</Text>;
  const truncated = text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
  return (
    <Tooltip title={text} placement="topLeft">
      {mono ? (
        <code style={{ fontSize: 12, cursor: 'default' }}>{truncated}</code>
      ) : (
        <span style={{ cursor: 'default' }}>{truncated}</span>
      )}
    </Tooltip>
  );
};

const LOCALES = [
  { value: 'ru', label: 'Русский (ru)' },
  { value: 'en', label: 'English (en)' },
  { value: 'uk', label: 'Українська (uk)' },
  { value: 'kk', label: 'Қазақша (kk)' },
  { value: 'uz', label: "O'zbek (uz)" },
  { value: 'az', label: 'Azərbaycan (az)' },
  { value: 'tr', label: 'Türkçe (tr)' },
  { value: 'de', label: 'Deutsch (de)' },
  { value: 'es', label: 'Español (es)' },
  { value: 'pt', label: 'Português (pt)' },
];

const COUNTRIES = [
  { value: 'RU', label: '🇷🇺 Россия' },
  { value: 'UA', label: '🇺🇦 Украина' },
  { value: 'KZ', label: '🇰🇿 Казахстан' },
  { value: 'UZ', label: '🇺🇿 Узбекистан' },
  { value: 'BY', label: '🇧🇾 Беларусь' },
  { value: 'AZ', label: '🇦🇿 Азербайджан' },
  { value: 'TR', label: '🇹🇷 Турция' },
  { value: 'DE', label: '🇩🇪 Германия' },
  { value: 'US', label: '🇺🇸 США' },
  { value: 'BR', label: '🇧🇷 Бразилия' },
  { value: 'IN', label: '🇮🇳 Индия' },
  { value: 'ID', label: '🇮🇩 Индонезия' },
];

const OS_OPTIONS = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
  { value: 'web', label: 'Web' },
];

const ANDROID_BRAND_COLOR = '#3ddc84';

interface FilterState {
  search: string;
  os: string;
  locale: string;
  country: string;
  has_registration: boolean | undefined;
  has_deposit: boolean | undefined;
  is_active: boolean | undefined;
  offer: string;
  buyer_id: string;
  dateRange: [Dayjs, Dayjs] | null;
}

const initialFilter: FilterState = {
  search: '',
  os: '',
  locale: '',
  country: '',
  has_registration: undefined,
  has_deposit: undefined,
  is_active: undefined,
  offer: '',
  buyer_id: '',
  dateRange: null,
};

const ApplicationUsers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();
  const { token } = theme.useToken();

  const application = useAppSelector(selectCurrentApplication);
  const users = useAppSelector(selectAppUsers);
  const stats = useAppSelector(selectAppUserStats);
  const isLoading = useAppSelector(selectApplicationsLoading);

  const [filters, setFilters] = useState<FilterState>(initialFilter);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchApplication(Number(id)));
      dispatch(fetchAppUserStats(Number(id)));
    }
    return () => {
      dispatch(clearAppUsers());
    };
  }, [id, dispatch]);

  const loadUsers = useCallback(() => {
    if (!id) return;

    const filter: AppUserFilter = {
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    };

    if (filters.search) filter.search = filters.search;
    if (filters.os) filter.os = filters.os;
    if (filters.locale) filter.locale = filters.locale;
    if (filters.country) filter.country = filters.country;
    if (filters.offer) filter.offer = filters.offer;
    if (filters.buyer_id) filter.buyer_id = filters.buyer_id;
    if (filters.has_registration !== undefined) filter.has_registration = filters.has_registration;
    if (filters.has_deposit !== undefined) filter.has_deposit = filters.has_deposit;
    if (filters.is_active !== undefined) filter.is_active = filters.is_active;
    if (filters.dateRange) {
      filter.date_from = filters.dateRange[0].toISOString();
      filter.date_to = filters.dateRange[1].toISOString();
    }

    dispatch(fetchAppUsers({ appId: Number(id), filter }))
      .unwrap()
      .then((response: { total?: number }) => {
        setTotal(response.total || 0);
      });
  }, [id, dispatch, pagination, filters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleFilterChange = (key: keyof FilterState, value: unknown) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setFilters(initialFilter);
    setPagination({ current: 1, pageSize: 20 });
  };

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
  ) => {
    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 20,
    });
  };

  const handleDeleteClick = (user: AppUser) => {
    setSelectedUser(user);
    setDeleteReason('');
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser || !id) return;

    try {
      await dispatch(deleteAppUser({
        appId: Number(id),
        userId: selectedUser.id,
        reason: deleteReason || 'Manual deletion from admin panel',
      })).unwrap();
      message.success('Пользователь успешно удалён');
      setDeleteModalVisible(false);
      setSelectedUser(null);
      loadUsers();
      dispatch(fetchAppUserStats(Number(id)));
    } catch {
      message.error('Не удалось удалить пользователя');
    }
  };

  const handleRestore = async (user: AppUser) => {
    if (!id) return;

    try {
      await dispatch(restoreAppUser({
        appId: Number(id),
        userId: user.id,
      })).unwrap();
      message.success('Пользователь успешно восстановлен');
      loadUsers();
      dispatch(fetchAppUserStats(Number(id)));
    } catch {
      message.error('Не удалось восстановить пользователя');
    }
  };

  const handleExport = async () => {
    if (!id) return;

    try {
      const filter: AppUserFilter = {};
      if (filters.os) filter.os = filters.os;
      if (filters.locale) filter.locale = filters.locale;
      if (filters.country) filter.country = filters.country;
      if (filters.has_registration !== undefined) filter.has_registration = filters.has_registration;
      if (filters.has_deposit !== undefined) filter.has_deposit = filters.has_deposit;
      if (filters.is_active !== undefined) filter.is_active = filters.is_active;

      const blob = await dispatch(exportAppUsers({
        appId: Number(id),
        filter,
      })).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `app_${id}_users_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Экспорт завершён');
    } catch {
      message.error('Не удалось экспортировать пользователей');
    }
  };

  const conversionRate = stats && stats.total_users > 0
    ? ((stats.with_deposit / stats.total_users) * 100).toFixed(1)
    : '0.0';

  const columns: ColumnsType<AppUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 65,
      fixed: 'left',
      render: (value: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Text>,
    },
    {
      title: 'External ID',
      dataIndex: 'user_external_id',
      key: 'user_external_id',
      width: 140,
      render: (value: string | NullString) => {
        const userId = getNullableString(value);
        return userId
          ? <TruncatedText text={userId} maxLen={12} mono />
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'AppsFlyer ID',
      dataIndex: 'appsflyer_id',
      key: 'appsflyer_id',
      width: 140,
      render: (value: string | NullString) => {
        const afId = getNullableString(value);
        return afId
          ? <TruncatedText text={afId} maxLen={12} mono />
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'OS',
      dataIndex: 'os',
      key: 'os',
      width: 90,
      render: (os: string) => {
        if (!os) return <Text type="secondary">—</Text>;
        const icon = os === 'android'
          ? <AndroidOutlined style={{ color: ANDROID_BRAND_COLOR }} />
          : os === 'ios'
            ? <AppleOutlined />
            : <GlobalOutlined style={{ color: token.colorPrimary }} />;
        const color = os === 'android' ? 'green' : os === 'ios' ? 'blue' : 'default';
        return (
          <Space size={4}>
            {icon}
            <Tag color={color} style={{ margin: 0 }}>{os.toUpperCase()}</Tag>
          </Space>
        );
      },
    },
    {
      title: 'Локаль',
      dataIndex: 'locale',
      key: 'locale',
      width: 75,
      render: (locale: string) => locale
        ? <Tag style={{ margin: 0 }}>{locale.toUpperCase()}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Страна',
      dataIndex: 'country',
      key: 'country',
      width: 80,
      render: (value: string | NullString) => {
        const country = getNullableString(value);
        return country
          ? <Tag color="geekblue" style={{ margin: 0 }}>{country.toUpperCase()}</Tag>
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Регистрация',
      dataIndex: 'has_registration',
      key: 'has_registration',
      width: 120,
      render: (hasReg: boolean, record) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            {hasReg
              ? <CheckCircleOutlined style={{ color: token.colorSuccess }} />
              : <CloseCircleOutlined style={{ color: token.colorTextQuaternary }} />
            }
            <Text>{hasReg ? 'Да' : 'Нет'}</Text>
          </Space>
          {record.registration_date && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatters.date(record.registration_date)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Депозит',
      dataIndex: 'has_deposit',
      key: 'has_deposit',
      width: 120,
      render: (hasDep: boolean, record) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            {hasDep
              ? <CheckCircleOutlined style={{ color: token.colorSuccess }} />
              : <CloseCircleOutlined style={{ color: token.colorTextQuaternary }} />
            }
            <Text>{hasDep ? 'Да' : 'Нет'}</Text>
          </Space>
          {record.deposit_date && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatters.date(record.deposit_date)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Offer',
      dataIndex: 'offer',
      key: 'offer',
      width: 130,
      ellipsis: true,
      render: (value: string | NullString) => {
        const offer = getNullableString(value);
        if (!offer) return <Text type="secondary">—</Text>;
        const label = offer.length > 14 ? `${offer.slice(0, 14)}…` : offer;
        return (
          <Tooltip title={offer} placement="topLeft">
            <Tag
              color="purple"
              style={{
                margin: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                cursor: 'default',
              }}
            >
              {label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Байер',
      dataIndex: 'buyer_id',
      key: 'buyer_id',
      width: 120,
      render: (value: string | NullString) => {
        const buyerID = getNullableString(value);
        return buyerID
          ? <TruncatedText text={buyerID} maxLen={12} mono />
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Установка',
      dataIndex: 'install_date',
      key: 'install_date',
      width: 100,
      render: (date: string) => (
        <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          {formatters.date(date)}
        </Text>
      ),
    },
    {
      title: 'Push Token',
      dataIndex: 'push_token',
      key: 'push_token',
      width: 120,
      render: (token_: string) => {
        if (!token_) return <Tag color="red" style={{ margin: 0 }}>Нет</Tag>;
        return (
          <Tooltip title={token_} placement="topLeft">
            <Tag color="blue" style={{ margin: 0, cursor: 'default' }}>
              {token_.slice(0, 10)}…
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Push ошибки',
      dataIndex: 'failed_push_attempts',
      key: 'failed_push_attempts',
      width: 95,
      align: 'center',
      render: (attempts: number) => (
        <Tag
          color={attempts === 0 ? 'default' : attempts >= 3 ? 'red' : 'orange'}
          style={{ margin: 0, minWidth: 32, textAlign: 'center' }}
        >
          {attempts}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 95,
      fixed: 'right',
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? 'success' : 'error'}
          text={
            <Text style={{ fontSize: 13 }}>
              {isActive ? 'Активен' : 'Удалён'}
            </Text>
          }
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      fixed: 'right',
      render: (_, record) => (
        record.is_active ? (
          <Tooltip title="Удалить пользователя">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteClick(record)}
              disabled={!permissions.canEditApplications}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Восстановить пользователя">
            <Popconfirm
              title="Восстановить пользователя?"
              onConfirm={() => handleRestore(record)}
              okText="Да"
              cancelText="Нет"
            >
              <Button
                type="text"
                size="small"
                icon={<UndoOutlined />}
                style={{ color: token.colorSuccess }}
                disabled={!permissions.canEditApplications}
              />
            </Popconfirm>
          </Tooltip>
        )
      ),
    },
  ];

  if (!application) {
    return (
      <div className="page-container">
        <Card loading={isLoading}>
          <div>Загрузка...</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <div className="page-header-content">
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(`/applications/${id}`)}
              />
              <div>
                <h1 style={{ margin: 0 }}>Пользователи: {application.name}</h1>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Управление пользователями приложения
                </Text>
              </div>
            </Space>
          </div>
          <div className="page-header-actions">
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
              >
                Фильтры
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Экспорт CSV
              </Button>
              <Button icon={<UploadOutlined />} onClick={() => setImportModalOpen(true)}>
                Импорт CSV
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadUsers} loading={isLoading}>
                Обновить
              </Button>
            </Space>
          </div>
        </div>
        {stats && (
          <Row gutter={12} style={{ marginBottom: 20, marginTop: 16 }}>
            <Col span={4}>
              <Card size="small" bordered style={{ borderRadius: 8 }}>
                <Statistic
                  title="Всего"
                  value={stats.total_users}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: token.colorPrimary, fontSize: 22 }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small" bordered style={{ borderRadius: 8 }}>
                <Statistic
                  title="Активных"
                  value={stats.active_users}
                  valueStyle={{ color: token.colorSuccess, fontSize: 22 }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small" bordered style={{ borderRadius: 8 }}>
                <Statistic
                  title="Удалённых"
                  value={stats.deleted_users}
                  valueStyle={{ color: token.colorError, fontSize: 22 }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small" bordered style={{ borderRadius: 8 }}>
                <Statistic
                  title="С регистрацией"
                  value={stats.with_registration}
                  valueStyle={{ color: token.purple, fontSize: 22 }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small" bordered style={{ borderRadius: 8 }}>
                <Statistic
                  title="С депозитом"
                  value={stats.with_deposit}
                  valueStyle={{ color: token.colorWarning, fontSize: 22 }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small" bordered style={{ borderRadius: 8 }}>
                <Statistic
                  title="Конверсия"
                  value={conversionRate}
                  suffix="%"
                  prefix={<RiseOutlined />}
                  valueStyle={{ color: token.cyan, fontSize: 22 }}
                />
              </Card>
            </Col>
          </Row>
        )}
        {showFilters && (
          <Card size="small" style={{ marginBottom: 16, background: token.colorFillQuaternary }}>
            <Form layout="vertical" size="small">
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item label="Поиск" style={{ marginBottom: 8 }}>
                    <Input
                      placeholder="ID, External ID, AppsFlyer ID..."
                      prefix={<SearchOutlined />}
                      value={filters.search}
                      onChange={e => handleFilterChange('search', e.target.value)}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Страна" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Выберите страну"
                      value={filters.country || undefined}
                      onChange={value => handleFilterChange('country', value)}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      style={{ width: '100%' }}
                    >
                      {COUNTRIES.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="OS" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Выберите OS"
                      value={filters.os || undefined}
                      onChange={value => handleFilterChange('os', value)}
                      allowClear
                      style={{ width: '100%' }}
                    >
                      {OS_OPTIONS.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Локаль" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Выберите локаль"
                      value={filters.locale || undefined}
                      onChange={value => handleFilterChange('locale', value)}
                      allowClear
                      showSearch
                      style={{ width: '100%' }}
                    >
                      {LOCALES.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Offer" style={{ marginBottom: 8 }}>
                    <Input
                      placeholder="Offer ID"
                      value={filters.offer}
                      onChange={e => handleFilterChange('offer', e.target.value)}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Байер" style={{ marginBottom: 8 }}>
                    <Input
                      placeholder="ID байера"
                      value={filters.buyer_id}
                      onChange={e => handleFilterChange('buyer_id', e.target.value)}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Дата установки" style={{ marginBottom: 8 }}>
                    <RangePicker
                      value={filters.dateRange}
                      onChange={dates => handleFilterChange('dateRange', dates)}
                      style={{ width: '100%' }}
                      format="DD.MM.YYYY"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12} align="bottom">
                <Col span={4}>
                  <Form.Item label="Регистрация" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Любой"
                      value={filters.has_registration}
                      onChange={value => handleFilterChange('has_registration', value)}
                      allowClear
                      style={{ width: '100%' }}
                    >
                      <Option value={true}>Есть</Option>
                      <Option value={false}>Нет</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Депозит" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Любой"
                      value={filters.has_deposit}
                      onChange={value => handleFilterChange('has_deposit', value)}
                      allowClear
                      style={{ width: '100%' }}
                    >
                      <Option value={true}>Есть</Option>
                      <Option value={false}>Нет</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Статус" style={{ marginBottom: 8 }}>
                    <Select
                      placeholder="Любой"
                      value={filters.is_active}
                      onChange={value => handleFilterChange('is_active', value)}
                      allowClear
                      style={{ width: '100%' }}
                    >
                      <Option value={true}>Активные</Option>
                      <Option value={false}>Удалённые</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12} style={{ paddingBottom: 8 }}>
                  <Button icon={<ClearOutlined />} onClick={handleResetFilters}>
                    Сбросить фильтры
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        )}
        {stats && users.length > 0 && (
          <SegmentSuggestions
            appId={Number(id)}
          />
        )}
        <Space style={{ marginBottom: 12 }} wrap size={6}>
          <Button
            size="small"
            type={filters.is_active === true ? 'primary' : 'default'}
            onClick={() => handleFilterChange('is_active', filters.is_active === true ? undefined : true)}
          >
            Активные ({stats?.active_users || 0})
          </Button>
          <Button
            size="small"
            type={filters.is_active === false ? 'primary' : 'default'}
            onClick={() => handleFilterChange('is_active', filters.is_active === false ? undefined : false)}
          >
            Удалённые ({stats?.deleted_users || 0})
          </Button>
          <Divider type="vertical" />
          <Button
            size="small"
            type={filters.has_registration === true ? 'primary' : 'default'}
            onClick={() => handleFilterChange('has_registration', filters.has_registration === true ? undefined : true)}
          >
            С регистрацией
          </Button>
          <Button
            size="small"
            type={filters.has_deposit === true ? 'primary' : 'default'}
            onClick={() => handleFilterChange('has_deposit', filters.has_deposit === true ? undefined : true)}
          >
            С депозитом
          </Button>
          <Divider type="vertical" />
          {Object.entries(stats?.by_os || {}).map(([os, count]) => (
            <Button
              key={os}
              size="small"
              type={filters.os === os ? 'primary' : 'default'}
              onClick={() => handleFilterChange('os', filters.os === os ? '' : os)}
              icon={
                os === 'android'
                  ? <AndroidOutlined />
                  : os === 'ios'
                    ? <AppleOutlined />
                    : <GlobalOutlined />
              }
            >
              {os.toUpperCase()} ({count})
            </Button>
          ))}
        </Space>
        <Table
          className="app-users-table"
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (totalItems, range) =>
              `${range[0]}–${range[1]} из ${totalItems}`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          size="middle"
          rowClassName={(record) => !record.is_active ? 'deleted-row' : ''}
        />
      </Card>
      <Modal
        title="Удаление пользователя"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedUser(null);
        }}
        okText="Удалить"
        cancelText="Отмена"
        okButtonProps={{ danger: true }}
      >
        <p>
          Вы уверены, что хотите удалить пользователя{' '}
          <strong>
            {getNullableString(selectedUser?.user_external_id) ||
              getNullableString(selectedUser?.appsflyer_id) ||
              `ID: ${selectedUser?.id}`}
          </strong>?
        </p>
        <p>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Пользователь будет деактивирован и его push-токен будет очищен.
          </Text>
        </p>
        <Form.Item label="Причина удаления (опционально)" style={{ marginBottom: 0 }}>
          <Input.TextArea
            rows={2}
            value={deleteReason}
            onChange={e => setDeleteReason(e.target.value)}
            placeholder="Например: Запрос пользователя, неактивность..."
          />
        </Form.Item>
      </Modal>

      <style>{`
        .app-users-table .deleted-row > td.ant-table-cell {
          background-color: ${token.colorErrorBg} !important;
        }
        .app-users-table .deleted-row:hover > td.ant-table-cell {
          background-color: ${token.colorErrorBgHover} !important;
        }
        .app-users-table .ant-table-cell {
          vertical-align: middle;
        }
      `}</style>

      {application && (
        <ImportUsersModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          application={application}
          baseURL="https://v2.pnsynd.com"
          onImportDone={() => {
            loadUsers();
            dispatch(fetchAppUserStats(Number(id)));
          }}
        />
      )}
    </div>
  );
};

export default ApplicationUsers;